import _ from 'lodash';
import { RQAPICallWithData } from 'store/APIClient';
import { snakify, camelize } from 'helpers/syntaxHelper';
import {
    FilterOperations,
    DataTypes,
    TimeScales,
    GetDataPayloadT,
} from 'business/definitions';
import {
    YearlyComparisonScale,
    isYearlyComparisonScale,
} from 'helpers/yearlyComparison/scale';
import { getYearlyComparisonSettingsForScale } from 'helpers/yearlyComparison/settings';
import { transformQueryToDataQuery } from 'helpers/queryHelpers';
import { isPolygon } from 'helpers/formatHelper';

type DownloadFormat = 'xlsx' | 'csv';

export type DetailsDataQueryT = {
    metric: QueryMetricT;
    dimensions: QueryDimensionT[];
    filters: QueryFilterT[];
    columns: ResponseColumnT[];
    dashboardFilters: QueryFilterT[];
    point: ResponseDataT | undefined;
    downloadFormat?: DownloadFormat;
};

type RawDataQueryParamsT = {
    queryMetrics: QueryMetricT;
    queryFilters: QueryFilterT[];
    dashboardFilters: QueryFilterT[];
    detailsFilters: QueryFilterT[];
    dimensions: QueryDimensionT[];
    downloadFormat?: DownloadFormat;
};

/** For raw data, we don't want to aggregate per year, month or week the date, we want the day.
 *  So we remove the known scale from the dimension.
 */
const setDateDimensionToDay = (dimension: QueryDimensionT): QueryDimensionT => {
    if (dimension.scale) {
        return {
            ...dimension,
            scale: 'day',
        };
    }
    return dimension;
};

/** For raw data, we don't want to add the default_time dimensions, as it's already in the filter
 *  if it's in the query, and it can create duplication that crash the query ->
 *  So we remove the known scale from the dimension.
 */
const removeDefaultTime = (dimension: QueryDimensionT) =>
    dimension.field !== 'default_time';

const getDetailsFilterOperation = (
    type: DataTypesT | null,
    scale: TimeScalesT | null | undefined,
): FilterOperationsT => {
    // For day of week, we have to use the extract operation
    if (scale === TimeScales.dow) {
        return FilterOperations.extract;
    }
    switch (type) {
        case DataTypes.category:
            return FilterOperations.in_;
        default:
            return FilterOperations.equal;
    }
};

const isPointValueUnknown = (pointValue: ResponseDataValueT) =>
    pointValue === 'unknown::' ||
    pointValue === 'unknown' ||
    pointValue === '-';
/**
 * Get and parse the value of a filter field from a point.
 * For tree object, we add a '::' at the end of the value if the children need to be query.
 * @param {str} filterField - the field to filter
 * @param {ResponseDataT} point - the point from which to get the value
 * @param {boolean} hasFilterAssociated - if the filter is associated to another filter in the request
 * @returns {number | string | Date | null | boolean}  - the parsed value of the filter
 */
const getDetailFilterValue = (
    filterField: string,
    point: ResponseDataT,
    hasFilterAssociated: boolean,
    scale: TimeScalesT | null | undefined,
): number | string | Date | null | boolean => {
    const pointValue = point[filterField];
    if (pointValue && typeof pointValue === 'object' && 'value' in pointValue) {
        if (pointValue.value === 'unknown') {
            return '';
        }
        return hasFilterAssociated ? `${pointValue.value}::` : pointValue.value;
    }
    if (isPointValueUnknown(pointValue)) {
        return null;
    }
    if (isPolygon(pointValue)) {
        console.warn("Polygon can't be used as a filter value");
        return '';
    }
    if (scale === TimeScales.dow && typeof pointValue === 'string') {
        // Map day names to numbers: "MONDAY" = 1, ..., "SUNDAY" = 0
        const dowMap: Record<string, number> = {
            SUNDAY: 0,
            MONDAY: 1,
            TUESDAY: 2,
            WEDNESDAY: 3,
            THURSDAY: 4,
            FRIDAY: 5,
            SATURDAY: 6,
        };
        const dow = dowMap[pointValue.toUpperCase()];
        return typeof dow === 'number' ? dow : null;
    }
    return pointValue;
};

const getDetailsIntervalFilter = (
    field: string,
    interval: number[],
): QueryFilterT[] => [
    {
        field,
        operation: FilterOperations.greater_or_equal,
        value: interval[0].toString(),
    },
    {
        field,
        operation: FilterOperations.lower,
        value: interval[1].toString(),
    },
];

const getDetailsFilter = (
    dimension: QueryDimensionT,
    point: ResponseDataT,
    type: DataTypesT | null,
    hasFilterAssociated: boolean,
    scale: TimeScalesT | null | undefined,
): QueryFilterT[] => {
    const filterField = dimension.field;
    const value = getDetailFilterValue(
        camelize(filterField),
        point,
        hasFilterAssociated,
        scale,
    );
    const interval = point.filter && point.filter[camelize(dimension.field)];
    if (interval) {
        return getDetailsIntervalFilter(dimension.field, interval);
    }

    // Check if value is valid, including 0 for day of week (dow)
    const isZeroValueDow = scale === TimeScales.dow && value === 0;
    const hasValidValue = value || isZeroValueDow;

    const queryFilter = {
        field: dimension.field,
        operation: hasValidValue
            ? getDetailsFilterOperation(type, scale)
            : FilterOperations.equal,
        value: hasValidValue ? value.toString() : null,
        ...(scale === TimeScales.dow && { valueScale: scale }),
    };
    return [queryFilter];
};

const getDetailTimeFilterValue = (date: Date): string =>
    `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;

const getDateWithoutTimezone = (date: Date): Date => {
    const dateString = `${date.getFullYear()}-${
        date.getMonth() + 1
    }-${date.getDate()}`;
    return new Date(`${dateString} 00:00:00Z`);
};

/**
 * Calculate the end date for a given start date and time scale.
 * @param startDate - The starting date.
 * @param scale - The time scale.
 * @returns The calculated end date.
 */
export const calculateEndDate = (
    startDate: Date,
    scale: TimeScalesT | undefined,
): string => {
    let endDate;
    switch (scale) {
        case 'day':
            endDate = new Date(startDate);
            break;
        case 'week':
        case 'sliding_week':
            endDate = new Date(startDate);
            // 7-day interval including startDate
            endDate.setDate(endDate.getDate() + 6);
            break;
        case 'month':
            // Last day of the month of startDate
            endDate = new Date(
                startDate.getFullYear(),
                startDate.getMonth() + 1,
                0,
            );
            break;
        case 'quarter':
            endDate = new Date(startDate);
            endDate.setMonth(endDate.getMonth() + 3); // 3 months ahead
            endDate.setDate(0); // Move back to last day of previous month (i.e. end of quarter)
            break;
        case 'year':
            endDate = new Date(startDate.getFullYear(), 11, 31);
            break;
        case 'sliding_year':
            endDate = new Date(startDate);
            endDate.setFullYear(endDate.getFullYear() + 1);
            endDate.setDate(endDate.getDate() - 1);
            break;
        case 'school_year': {
            // School year: ends August 31st of the next year if start is Sept or later, else this year
            const year =
                startDate.getMonth() >= 8
                    ? startDate.getFullYear() + 1
                    : startDate.getFullYear();
            endDate = new Date(year, 7, 31); // August is month 7 (0-based)
            break;
        }
        default:
            endDate = new Date(startDate);
            break;
    }
    return getDetailTimeFilterValue(getDateWithoutTimezone(endDate));
};

const getDetailsTimeFilter = (
    dimension: QueryDimensionT,
    point: ResponseDataT,
): QueryFilterT[] => {
    const filterField = dimension.field;
    const value = point[camelize(filterField)];

    if (value === 'unknown' || value === null) {
        // we don't have any known value, return an empty filter
        return [
            {
                field: filterField,
                operation: FilterOperations.equal,
                value: null,
            },
        ];
    }

    const date = new Date(parseInt(value.toString(), 10)); // parseInt(string()) used because of ResponseDataValueT

    const start = getDetailTimeFilterValue(getDateWithoutTimezone(date));
    const end = calculateEndDate(date, dimension.scale);

    const queryFilter = {
        field: filterField,
        operation: FilterOperations.between as FilterOperationsT,
        value: `${start}|${end}`,
        valueScale: TimeScales.day as TimeScalesT,
    };
    return [queryFilter];
};

export const getQueryFilters = (
    metric: QueryMetricT,
    point: ResponseDataT,
): QueryFilterT[] => {
    const pointId = point[camelize(metric.field)];
    // if pointId exists, there is a single inscription to display (static or pivot table cell)
    if (pointId) {
        return [
            {
                field: `${metric.field}.id`,
                operation: FilterOperations.equal,
                value: pointId.toString(),
            },
        ];
    }
    // otherwise user is in pivot table mode with several inscriptions aggregated within the cell
    return _.map(
        point,
        (value, field): QueryFilterT => ({
            field: snakify(field),
            operation: FilterOperations.equal,
            // TODO [typing getData hooks] try to remove this non-null assertion
            value: value!.toString(),
        }),
    );
};

export const getDetailsFiltersFromDimension = (
    dimensions: QueryDimensionT[],
    point: ResponseDataT,
    columns: ResponseColumnT[],
    filters: QueryFilterT[],
): QueryFilterT[] => {
    const queryFilters: QueryFilterT[] = [];
    dimensions
        .filter((dimension) => point && camelize(dimension.field) in point)
        .forEach((dimension) => {
            const column = columns.filter(
                (col) => col.key === camelize(dimension.field),
            )[0];
            const type = column ? column.type : null;
            const scale = column ? column.scale : null;
            const filterAssociated =
                !point.isParent &&
                filters.find((f) => f.field === dimension.field);
            if (type === DataTypes.date) {
                queryFilters.push(...getDetailsTimeFilter(dimension, point));
            } else {
                queryFilters.push(
                    ...getDetailsFilter(
                        dimension,
                        point,
                        type,
                        Boolean(filterAssociated),
                        scale,
                    ),
                );
            }
        });
    return queryFilters;
};

const getYearlyComparisonScaleFromPoint = (
    point: ResponseDataT,
): YearlyComparisonScale | undefined =>
    Object.keys(point).find((key) => isYearlyComparisonScale(key)) as
        | YearlyComparisonScale
        | undefined;

const enrichQueryFiltersFromPivotedDate = (
    queryFilters: QueryFilterT[],
    point: ResponseDataT,
    dimensions: QueryDimensionT[],
): void => {
    if (!point) return;

    const yearlyComparisonScale = getYearlyComparisonScaleFromPoint(point);
    if (!yearlyComparisonScale) return;

    const yearlyComparisonsettings = getYearlyComparisonSettingsForScale(
        yearlyComparisonScale,
    );

    const [valueDimension] = dimensions.filter(
        (dimension) =>
            !(point && dimension.field in point) &&
            dimension.scale &&
            dimension.scale === yearlyComparisonScale,
    );

    const pointYearlyFilters = yearlyComparisonsettings.getDetailFilter(
        point,
        valueDimension,
    );
    queryFilters.push(...pointYearlyFilters);
};

const getDetailsDataQueryFromRawDataQuery = ({
    queryMetrics,
    queryFilters,
    dashboardFilters,
    detailsFilters,
    dimensions,
}: RawDataQueryParamsT): QueryT => ({
    metrics: [queryMetrics],
    filters: queryFilters,
    dashboardFilters,
    detailsFilters,
    dimensions: dimensions.filter(removeDefaultTime).map(setDateDimensionToDay),
    add_default_dimensions: true,
});

export const getDetailsDataQuery = (
    payload: DetailsDataQueryT,
): QueryT | undefined => {
    const { metric, point, dimensions, columns, filters, dashboardFilters } =
        payload;

    if (point === undefined) {
        return undefined;
    }

    const queryMetrics = {
        field: metric.field.split('.')[0],
        aggregation: 'list',
    };

    // handle the list case
    if (metric.aggregation === 'list') {
        const detailsFilters: QueryFilterT[] = getQueryFilters(metric, point);
        return getDetailsDataQueryFromRawDataQuery({
            queryMetrics,
            queryFilters: filters,
            dashboardFilters,
            detailsFilters,
            dimensions: [],
        });
    }

    // Create filters based on the request dimensions
    const detailsFilters = getDetailsFiltersFromDimension(
        dimensions,
        point,
        columns,
        filters,
    );
    // If the data are pivoted for a pluriannual plot, we add the month to the filters
    enrichQueryFiltersFromPivotedDate(detailsFilters, point, dimensions);
    return getDetailsDataQueryFromRawDataQuery({
        queryMetrics,
        queryFilters: filters,
        dashboardFilters,
        detailsFilters,
        dimensions,
    });
};

export const getDataQuery = async (
    query: QueryT | undefined,
): Promise<GetDataPayloadT['data']> => {
    const response = await RQAPICallWithData<GetDataPayloadT['data']>({
        endpoint: `query/get_data/v2`,
        method: 'POST',
        body: transformQueryToDataQuery(query),
    });

    return response.content.data;
};
